import React, { useState } from "react";
import { trackEvent } from "@leafly-com/web-utils";
import Link from "next/link";

import getListCampaignSections from "@/api/requests/getListCampaignSections";
import useOnComponentInView from "@/hooks/useOnComponentInView";

import Image from "@/components/Image";
import Placeholder, { Rect } from "@/components/Placeholder";
import TrackImpression from "@/components/TrackImpression";

export type CampaignSection = {
  section_header: string;
  url: string;
  mobile_image: string;
  desktop_image: string;
  title: string;
  subtitle: string;
  button_label: string;
};

const ListCampaignSection = () => {
  const [campaignData, setCampaignData] = useState<CampaignSection>();
  const [loading, setLoading] = useState<boolean>(true);

  const [ref] = useOnComponentInView(
    async () => {
      setLoading(true);

      const data = await getListCampaignSections();

      setCampaignData(data as CampaignSection);
      setLoading(false);
    },
    { triggerOnce: true },
  );

  return (
    <div ref={ref} className="mt-xxl">
      {!loading && campaignData ? (
        <TrackImpression
          category="Strain List Feature"
          label={campaignData.section_header}
        >
          <div className="relative campaign-wrapper mt-sm">
            <Link
              onClick={() =>
                trackEvent(
                  "Strain List Feature",
                  "Click",
                  campaignData.section_header,
                )
              }
              href={campaignData.url}
            >
              <Image
                alt="Campaign feature"
                className="strains-list-campaign-gradient rounded overflow-hidden shadow-low"
                background={true}
                mobileSrc={campaignData.mobile_image}
                sizes={[544, 767, 1023, 1440, null, 767]}
                src={campaignData.desktop_image}
                tabletSrc={campaignData.desktop_image}
              />
              <div className="campaign-feature-overlay">
                <div className="text-lg font-extrabold text-white">
                  {campaignData.title}
                </div>
                <div className="hidden md:block mt-lg text-white">
                  {campaignData.subtitle}
                </div>

                <div className="campaign-cta">
                  <div className="button button--secondary campaign-cta mt-lg bg-white">
                    {campaignData.button_label}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </TrackImpression>
      ) : loading ? (
        <Placeholder height="300" width="100%" className="rounded">
          <Rect width="100%" height="100%" />
        </Placeholder>
      ) : (
        <></>
      )}

      <style jsx>{`
        .campaign-wrapper {
          height: 290px;
        }

        .campaign-feature-overlay {
          position: absolute;
          bottom: 28px;
          left: 18px;
          right: 30px;
        }

        .campaign-cta {
          max-width: 288px;
        }

        @media (min-width: 768px) {
          .campaign-wrapper {
            height: 400px;
          }

          .campaign-overlay {
            position: absolute;
            bottom: 32px;
            left: 66px;
            right: 58px;
          }
        }
      `}</style>
      <style global jsx>{`
        .strains-list-campaign-gradient {
          position: relative;
        }

        .strains-list-campaign-gradient:after {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0) 0%,
            #000000 95.75%
          );
        }
      `}</style>
    </div>
  );
};

export default ListCampaignSection;
